import React from "react";
import {
  GithubOutlined,
  GlobalOutlined,
  LinkedinOutlined,
  LinkOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { theme } from "antd";
import { useResumeStore } from "../../../store/resumeStore.jsx";
import {
  formatDateRange,
  getAchievements,
  getActiveEducation,
  getActiveExperiences,
  getAboutText,
  getContactItems,
  getExperienceHighlights,
  getInitials,
  getLanguages,
  getObjectiveText,
  getPrimaryContactItems,
  getSkills,
  getSocialItems,
  getSummaryText,
  normalizeExternalUrl,
  stripHtml,
} from "../resumeTemplateUtils.js";

const { useToken } = theme;
const DEFAULT_THEME_PRIMARY = "#1677ff";

function normalizeColor(value = "") {
  return String(value || "").trim().toLowerCase();
}

function hasThemeAccent(token) {
  const primary = normalizeColor(token?.colorPrimary);
  return Boolean(primary && primary !== DEFAULT_THEME_PRIMARY);
}

function getTemplateAccent(token, fallback) {
  return hasThemeAccent(token) ? token.colorPrimary : fallback;
}

function withAlpha(color, alpha = "18") {
  return /^#[0-9a-f]{6}$/i.test(color) ? `${color}${alpha}` : color;
}

function getRailNameTypography(name = "") {
  const normalizedName = cleanDisplayValue(name);
  const characterCount = normalizedName.replace(/\s/g, "").length;
  const wordCount = normalizedName.split(/\s+/).filter(Boolean).length;
  const longestWord = normalizedName
    .split(/\s+/)
    .reduce((longest, word) => Math.max(longest, word.length), 0);

  if (characterCount > 34 || longestWord > 16 || wordCount > 4) {
    return { fontSize: 17.5, letterSpacing: "0.015em", lineHeight: 1.18 };
  }

  if (characterCount > 26 || longestWord > 12 || wordCount > 3) {
    return { fontSize: 19.5, letterSpacing: "0.025em", lineHeight: 1.16 };
  }

  if (characterCount > 18) {
    return { fontSize: 21.5, letterSpacing: "0.035em", lineHeight: 1.14 };
  }

  return { fontSize: 24, letterSpacing: "0.045em", lineHeight: 1.12 };
}

function themedTint(token, fallback, alpha = "18") {
  const accent = getTemplateAccent(token, fallback);
  return hasThemeAccent(token) ? withAlpha(accent, alpha) : fallback;
}

function cleanDisplayValue(value) {
  if (value == null || value === false) return "";
  if (React.isValidElement(value)) return "";

  if (Array.isArray(value)) {
    return value.map(cleanDisplayValue).filter(Boolean).join(", ");
  }

  if (typeof value === "object") {
    return cleanDisplayValue(
      value.value ||
        value.label ||
        value.name ||
        value.title ||
        value.text ||
        value.description ||
        "",
    );
  }

  return stripHtml(String(value))
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function renderText(value) {
  if (value == null || value === false) return "";
  if (React.isValidElement(value)) return value;
  if (typeof value === "string" || typeof value === "number") {
    return cleanDisplayValue(value);
  }
  if (Array.isArray(value)) {
    return value.map(renderText).filter(Boolean).join(", ");
  }
  if (typeof value === "object") {
    return cleanDisplayValue(
      value.text ||
        value.title ||
        value.description ||
        value.name ||
        value.label ||
        value.value ||
        "",
    );
  }
  return "";
}

function normalizeRenderItems(items = []) {
  return items
    .map(renderText)
    .filter((item) => React.isValidElement(item) || cleanDisplayValue(item));
}

function firstEducationValue(item, keys) {
  for (const key of keys) {
    const value = key.split(".").reduce((source, part) => source?.[part], item);
    const cleaned = cleanDisplayValue(value);
    if (cleaned) return cleaned;
  }

  return "";
}

function getEducationTitle(item) {
  return firstEducationValue(item, [
    "degree",
    "course",
    "qualification",
    "title",
    "accreditation.education",
    "accreditation.name",
    "field",
  ]) || "Education";
}

function getEducationRows(item) {
  const title = getEducationTitle(item).toLowerCase();
  const date =
    formatDateRange(item.from, item.to, item.years) ||
    firstEducationValue(item, ["duration", "date", "year", "startYear", "endYear", "passingYear"]);
  const rows = [
    {
      field: "institution",
      label: "Institution",
      value: firstEducationValue(item, [
        "institution",
        "organization",
        "university",
        "college",
        "school",
        "schoolName",
        "collegeName",
        "universityName",
        "name",
        "accreditation.organization",
      ]),
    },
    {
      field: "field",
      label: "Field",
      value: firstEducationValue(item, ["field", "major", "specialization", "area", "studyField"]),
    },
    {
      field: "location",
      label: "Location",
      value: firstEducationValue(item, ["location", "city", "state", "country"]),
    },
    {
      field: "years",
      label: "Years",
      value: date,
    },
    {
      field: "score",
      label: "Score",
      value: firstEducationValue(item, ["score", "grade.value", "grade", "gpa", "cgpa", "percentage"]),
    },
    {
      field: "board",
      label: "Board",
      value: firstEducationValue(item, ["board"]),
    },
    {
      field: "description",
      label: "Details",
      value: firstEducationValue(item, ["description"]),
    },
  ];
  const seen = new Set([title]);

  return rows.filter((row) => {
    const value = cleanDisplayValue(row.value);
    const key = value.toLowerCase();
    if (!value || seen.has(key)) return false;
    seen.add(key);
    row.value = value;
    return true;
  });
}

function collectLooseListValues(value) {
  if (React.isValidElement(value)) return [];

  if (Array.isArray(value)) {
    return value.flatMap((item) => collectLooseListValues(item));
  }

  if (value && typeof value === "object") {
    const preferred = cleanDisplayValue(
      value.title ||
        value.name ||
        value.label ||
        value.value ||
        value.description ||
        value.organization ||
        value.company ||
        "",
    );
    return preferred ? [preferred] : Object.values(value).flatMap((item) => collectLooseListValues(item));
  }

  const cleaned = cleanDisplayValue(value);
  if (!cleaned) return [];

  return cleaned
    .split(/\n|;|\|/g)
    .map((item) => cleanDisplayValue(item))
    .filter(Boolean);
}

function getOptionalList(data, keys, limit = Number.MAX_SAFE_INTEGER) {
  const seen = new Set();

  return keys
    .flatMap((key) => collectLooseListValues(key.split(".").reduce((source, part) => source?.[part], data)))
    .filter((item) => {
      const normalized = item.toLowerCase();
      if (!normalized || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    })
    .slice(0, limit);
}

function formatReferenceLinkLabel(rawUrl = "") {
  try {
    const hostname = new URL(normalizeExternalUrl(rawUrl)).hostname.replace(/^www\./i, "");
    const knownDomains = {
      "linkedin.com": "LinkedIn",
      "github.com": "GitHub",
      "behance.net": "Behance",
      "dribbble.com": "Dribbble",
      "medium.com": "Medium",
      "x.com": "X",
      "twitter.com": "Twitter",
    };

    if (knownDomains[hostname]) return knownDomains[hostname];

    const domainName = hostname.split(".")[0] || hostname;
    return domainName
      .split(/[-_]/g)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  } catch {
    return "";
  }
}

function getLinkedValue(value, fallbackLabel = "") {
  const cleaned = cleanDisplayValue(value);
  if (!cleaned) return null;

  const emailMatch = cleaned.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  if (emailMatch) {
    return {
      href: `mailto:${emailMatch[0]}`,
      label: fallbackLabel || emailMatch[0],
    };
  }

  const urlMatch = cleaned.match(
    /(?:https?:\/\/)?(?:www\.)?(?:linkedin\.com|github\.com|[\w.-]+\.[a-z]{2,})(?:\/[^\s,)]+)?/i,
  );
  if (!urlMatch) return null;

  const rawUrl = urlMatch[0];
  const displayUrl = rawUrl.replace(/^https?:\/\//i, "").replace(/^www\./i, "");

  return {
    href: normalizeExternalUrl(rawUrl),
    label: fallbackLabel || formatReferenceLinkLabel(rawUrl) || displayUrl,
  };
}

function getSocialLabel(key = "") {
  if (key === "linkedin") return "LinkedIn";
  if (key === "github") return "GitHub";
  return key ? key.charAt(0).toUpperCase() + key.slice(1) : "";
}

function getReferenceItems(model) {
  const manualReferences = model.references.map((value, index) => ({
    key: `reference-${index}`,
    value,
    dataField: "reference",
  }));
  const socialReferences = model.socials.map((item) => ({
    key: `social-${item.key}`,
    value: item.value,
    label: getSocialLabel(item.key),
    dataField: "social",
  }));

  return [...manualReferences, ...socialReferences].filter((item, index, collection) => {
    const valueKey = cleanDisplayValue(item.value).toLowerCase();
    return valueKey && collection.findIndex((candidate) => cleanDisplayValue(candidate.value).toLowerCase() === valueKey) === index;
  });
}

function useTemplateModel() {
  const data = useResumeStore((state) => state.resume);
  return {
    data,
    name: data?.name || "Your Name",
    role: data?.roleTitle || data?.role || "Professional Title",
    about: getAboutText(data),
    objective: getObjectiveText(data),
    summary: getSummaryText(data),
    experiences: getActiveExperiences(data),
    education: getActiveEducation(data),
    skills: getSkills(data),
    languages: getLanguages(data),
    contacts: getContactItems(data),
    primaryContacts: getPrimaryContactItems(data),
    socials: getSocialItems(data),
    achievements: getAchievements(data),
    certifications: getOptionalList(data, ["certifications", "certification", "certificates", "licenses"], 8),
    hobbies: getOptionalList(data, ["hobbies", "interests", "interestsAndHobbies"], 8),
    references: getOptionalList(data, ["references", "referees"], 4),
    initials: getInitials(data?.name || "CV"),
  };
}

function Shell({ children, background = "#ffffff", color = "#1f2937", padding = 28 }) {
  return (
    <div
      role="document"
      data-resume-root="true"
      data-template-family="sample-inspired"
      style={{
        width: "100%",
        minHeight: "100%",
        background,
        color,
        padding,
        fontFamily: "Arial, Helvetica, sans-serif",
        fontSize: 13.2,
        lineHeight: 1.38,
        boxSizing: "border-box",
        overflowWrap: "break-word",
        wordBreak: "normal",
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
      }}
    >
      {children}
    </div>
  );
}

function PhotoFrame({ data, initials, size = 92, style = {}, square = false }) {
  return data?.photo ? (
    <img
      src={data.photo}
      alt="Profile"
      data-field="photo"
      style={{
        width: size,
        height: size,
        borderRadius: square ? 20 : "50%",
        objectFit: "cover",
        display: "block",
        ...style,
      }}
    />
  ) : (
    <div
      data-field="photo"
      aria-label="Profile initials"
      style={{
        width: size,
        height: size,
        borderRadius: square ? 20 : "50%",
        display: "grid",
        placeItems: "center",
        background: "#334155",
        color: "#ffffff",
        fontWeight: 800,
        fontSize: Math.max(18, size * 0.32),
        ...style,
      }}
    >
      {initials || "CV"}
    </div>
  );
}

function ContactRows({ items, color = "#374151", iconColor = "#6b7280", compact = false }) {
  if (!items.length) return null;

  const icons = {
    location: "⌂",
    phone: "☎",
    email: "✉",
    linkedin: "in",
    github: "GH",
  };

  return (
    <div style={{ display: "grid", gap: compact ? 2 : 4 }}>
      {items.map((item) => (
        <div
          key={item.key}
          data-section="contactItem"
          style={{
            display: "grid",
            gridTemplateColumns: compact ? "14px minmax(0, 1fr)" : "18px minmax(0, 1fr)",
            gap: compact ? 4 : 6,
            alignItems: "start",
            color,
            fontSize: compact ? 11.1 : 12.1,
            lineHeight: 1.2,
          }}
        >
          <span style={{ color: iconColor, fontWeight: 800 }}>{icons[item.key] || "•"}</span>
          {["email", "linkedin", "github"].includes(item.key) ? (
            <LinkedText
              value={item.value}
              dataField={item.key}
              color={color}
              accent={iconColor}
            />
          ) : (
            <span data-field={item.key} style={{ minWidth: 0, overflowWrap: "break-word" }}>
              {item.value}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function SectionTitle({ children, color = "#1f2937", accent = "#4c1d95", align = "left", line = false }) {
  return (
    <h2
      data-title="true"
      style={{
        margin: "0 0 7px",
        color,
        fontSize: 14.2,
        lineHeight: 1.2,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        fontWeight: 800,
        textAlign: align,
        display: line ? "flex" : "block",
        alignItems: "center",
        gap: 10,
      }}
    >
      <span style={{ borderBottom: `2px solid ${accent}`, paddingBottom: 2 }}>{children}</span>
      {line && <span style={{ flex: 1, height: 1, background: accent, opacity: 0.45 }} />}
    </h2>
  );
}

function SummaryBlock({ summary, style = {}, dataField = "summary" }) {
  if (!summary) return null;
  return (
    <p
      data-field={dataField}
      style={{
        margin: 0,
        fontSize: 12.5,
        lineHeight: 1.38,
        overflowWrap: "break-word",
        ...style,
      }}
    >
      {summary}
    </p>
  );
}

function hasProfileText(model) {
  return Boolean(model?.about || model?.objective);
}

function ProfileTextBlocks({
  model,
  textStyle = {},
  labelStyle = {},
  blockStyle = {},
  style = {},
}) {
  const entries = [
    { label: "About Me", value: model.about, field: "summary" },
    { label: "Career Objective", value: model.objective, field: "objective" },
  ].filter((entry, index, collection) => {
    if (!entry.value) return false;
    const normalized = cleanDisplayValue(entry.value).toLowerCase();
    return collection.findIndex((candidate) => cleanDisplayValue(candidate.value).toLowerCase() === normalized) === index;
  });

  if (!entries.length) return null;

  return (
    <div style={{ display: "grid", gap: entries.length > 1 ? 11 : 0, ...style }}>
      {entries.map((entry) => (
        <div key={entry.field} data-section={entry.field} style={blockStyle}>
          <h2
            style={{
              margin: "0 0 5px",
              fontSize: 15.5,
              lineHeight: 1.18,
              fontWeight: 900,
              letterSpacing: "0.01em",
              textTransform: "none",
              color: "#475569",
              ...labelStyle,
            }}
          >
            {entry.label}
          </h2>
          <SummaryBlock summary={entry.value} dataField={entry.field} style={textStyle} />
        </div>
      ))}
    </div>
  );
}

function EducationList({
  education,
  accent = "#2563eb",
  compact = false,
  titleColor = "#111827",
  mutedColor = "#64748b",
}) {
  const visibleEducation = education.filter((item) => {
    const title = getEducationTitle(item);
    const institution = firstEducationValue(item, [
      "institution",
      "organization",
      "university",
      "college",
      "school",
      "schoolName",
      "collegeName",
      "universityName",
      "name",
      "accreditation.organization",
    ]);

    return title !== "Education" || Boolean(institution);
  });

  if (!visibleEducation.length) return null;

  return (
    <div style={{ display: "grid", gap: compact ? 3 : 6 }}>
      {visibleEducation.map((item, index) => {
        const title = getEducationTitle(item);
        const rows = getEducationRows(item);

        return (
          <article
            key={index}
            data-section="educationItem"
            className="cv-group"
            style={{
              minWidth: 0,
              overflowWrap: "break-word",
              breakInside: "avoid",
              pageBreakInside: "avoid",
            }}
          >
            <div
              data-field="degree"
              style={{
                fontWeight: 800,
                color: titleColor,
                lineHeight: 1.3,
                fontSize: compact ? 13 : 14.5,
              }}
            >
              {title}
            </div>
            {rows.length > 0 && (
              <div style={{ display: "grid", gap: compact ? 1 : 3, marginTop: 3 }}>
                {rows.map((row) => (
                  <div
                    key={`${row.field}-${row.value}`}
                    data-field={row.field}
                    style={{
                      display: "grid",
                      gridTemplateColumns: compact ? "1fr" : "74px minmax(0, 1fr)",
                      gap: compact ? 1 : 8,
                      color: row.field === "years" || row.field === "score" ? accent : mutedColor,
                      fontSize: compact ? 12 : 12.5,
                      lineHeight: 1.34,
                    }}
                  >
                    {!compact && (
                      <span
                        style={{
                          color: accent,
                          fontSize: 11.5,
                          fontWeight: 800,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                        }}
                      >
                        {row.label}
                      </span>
                    )}
                    <span style={{ minWidth: 0 }}>{compact ? row.value : row.value}</span>
                  </div>
                ))}
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}

function AchievementList({ achievements, color = "#111827" }) {
  const visibleAchievements = normalizeRenderItems(achievements);
  if (!visibleAchievements.length) return null;
  return (
    <ul style={{ margin: 0, paddingLeft: 18, color }}>
      {visibleAchievements.map((item, index) => (
        <li key={index} data-field="achievements" style={{ marginBottom: 4, lineHeight: 1.35 }}>
          {item}
        </li>
      ))}
    </ul>
  );
}

function LinkedText({ value, label = "", dataField = "reference", color = "inherit", accent = "#2563eb" }) {
  const link = getLinkedValue(value, label);

  if (!link) {
    return (
      <span data-field={dataField} style={{ color, overflowWrap: "anywhere" }}>
        {cleanDisplayValue(value)}
      </span>
    );
  }

  return (
    <a
      data-field={dataField}
      href={link.href}
      target="_blank"
      rel="noreferrer"
      style={{
        color: accent,
        textDecoration: "underline",
        textUnderlineOffset: 2,
        overflowWrap: "anywhere",
      }}
    >
      {link.label}
    </a>
  );
}

function LinkedList({ items, color = "#374151", accent = "#2563eb", dataField = "reference" }) {
  if (!items.length) return null;

  return (
    <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none", display: "grid", gap: 4, color, fontSize: 12.2, lineHeight: 1.35 }}>
      {items.map((item, index) => (
        <li key={item.key || `${dataField}-${index}`} data-section={`${item.dataField || dataField}Item`} style={{ minWidth: 0 }}>
          <LinkedText
            value={item.value || item}
            label={item.label || ""}
            dataField={item.dataField || dataField}
            color={color}
            accent={accent}
          />
        </li>
      ))}
    </ul>
  );
}

function LinkIconStrip({ model, accent = "#2563eb", color = "#374151", background = "transparent", align = "flex-start" }) {
  const items = getReferenceItems(model).slice(0, 5);
  if (!items.length) return null;

  const getIcon = (label = "", href = "") => {
    const normalized = `${label} ${href}`.toLowerCase();
    if (normalized.includes("linkedin")) return <LinkedinOutlined />;
    if (normalized.includes("github")) return <GithubOutlined />;
    if (normalized.includes("mailto:") || normalized.includes("email") || normalized.includes("mail")) return <MailOutlined />;
    if (normalized.includes("portfolio") || normalized.includes("website") || normalized.includes("http")) return <GlobalOutlined />;
    return <LinkOutlined />;
  };

  return (
    <div
      data-section="socialLinks"
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: align,
        gap: "5px 7px",
        marginTop: 8,
      }}
    >
      {items.map((item, index) => {
        const link = getLinkedValue(item.value || item, item.label || "");
        const label = link?.label || item.label || cleanDisplayValue(item.value || item);
        const content = (
          <span
            aria-hidden="true"
            style={{
              display: "inline-grid",
              placeItems: "center",
              color,
              fontSize: 13,
              lineHeight: 1,
            }}
          >
            {getIcon(label, link?.href)}
          </span>
        );
        const style = {
          display: "inline-grid",
          placeItems: "center",
          alignItems: "center",
          width: 25,
          height: 25,
          minWidth: 25,
          border: `1px solid ${withAlpha(accent, "44")}`,
          borderRadius: "50%",
          padding: 0,
          background,
          color,
          fontSize: 13,
          fontWeight: 700,
          lineHeight: 1,
          textDecoration: "none",
          flex: "0 0 auto",
        };

        return link ? (
          <a
            key={`${label}-${index}`}
            data-field={item.dataField || "reference"}
            href={link.href}
            target="_blank"
            rel="noreferrer"
            aria-label={label}
            title={label}
            style={style}
          >
            {content}
          </a>
        ) : (
          <span
            key={`${label}-${index}`}
            data-field={item.dataField || "reference"}
            aria-label={label}
            title={label}
            style={style}
          >
            {content}
          </span>
        );
      })}
    </div>
  );
}

function SupplementalSections({
  model,
  accent,
  color = "#374151",
  titleColor,
  panelStyle = {},
  skip = {},
}) {
  const references = getReferenceItems(model);
  const headingColor = titleColor || color;
  const sectionStyle = { marginTop: 10, ...panelStyle };

  return (
    <>
      {!skip.achievements && model.achievements.length > 0 && (
        <section className="cv-section" data-section="achievements" style={sectionStyle}>
          <SectionTitle accent={accent} color={headingColor}>Achievements</SectionTitle>
          <AchievementList achievements={model.achievements} color={color} />
        </section>
      )}
      {!skip.certifications && model.certifications.length > 0 && (
        <section className="cv-section" data-section="certifications" style={sectionStyle}>
          <SectionTitle accent={accent} color={headingColor}>Certifications</SectionTitle>
          <LinkedList items={model.certifications} dataField="certification" color={color} accent={accent} />
        </section>
      )}
      {!skip.references && references.length > 0 && (
        <section className="cv-section" data-section="references" style={sectionStyle}>
          <LinkedList items={references} color={color} accent={accent} />
        </section>
      )}
      {!skip.languages && model.languages.length > 0 && (
        <section className="cv-section" data-section="languages" style={sectionStyle}>
          <SectionTitle accent={accent} color={headingColor}>Languages</SectionTitle>
          <PlainBulletList items={model.languages} dataField="language" color={color} />
        </section>
      )}
      {!skip.hobbies && model.hobbies.length > 0 && (
        <section className="cv-section" data-section="hobbies" style={sectionStyle}>
          <SectionTitle accent={accent} color={headingColor}>Hobbies</SectionTitle>
          <PlainBulletList items={model.hobbies} dataField="hobby" color={color} />
        </section>
      )}
    </>
  );
}

function PlainBulletList({
  items,
  dataField = "skill",
  color = "#374151",
  marker = "·",
  limit = Number.MAX_SAFE_INTEGER,
}) {
  const visibleItems = normalizeRenderItems(items).slice(0, limit);
  if (!visibleItems.length) return null;
  return (
    <ul style={{ margin: 0, paddingLeft: 0, color, listStyle: "none", fontSize: 12.5, lineHeight: 1.38 }}>
      {visibleItems.map((item, index) => (
        <li key={cleanDisplayValue(item) || index} data-section={`${dataField}Item`} data-field={dataField} style={{ marginBottom: 3 }}>
          {marker ? <span aria-hidden="true" style={{ marginRight: 5 }}>{marker}</span> : null}
          {item}
        </li>
      ))}
    </ul>
  );
}

function PurpleExpertiseList({ skills, accent }) {
  return <CompactChipList items={skills} accent={accent} />;
}

function CompactChipList({
  items,
  accent = "#6b7280",
  limit = Number.MAX_SAFE_INTEGER,
  dataField = "skill",
  color = "#1f2937",
  background = "#ffffff",
  borderColor,
}) {
  const visibleItems = normalizeRenderItems(items).slice(0, limit);
  if (!visibleItems.length) return null;
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "4px 5px",
        alignItems: "center",
      }}
    >
      {visibleItems.map((item, index) => (
        <span
          key={cleanDisplayValue(item) || index}
          data-section={`${dataField}Item`}
          data-field={dataField}
          style={{
            display: "inline-flex",
            alignItems: "center",
            width: "auto",
            maxWidth: "100%",
            border: `1px solid ${borderColor || withAlpha(accent, "55")}`,
            borderRadius: 30,
            padding: "2px 8px",
            background,
            color,
            fontSize: 11.2,
            fontWeight: 600,
            lineHeight: 1.18,
            overflowWrap: "anywhere",
            breakInside: "avoid",
            pageBreakInside: "avoid",
          }}
        >
          {item}
        </span>
      ))}
    </div>
  );
}

function BlushSkillNarrative({ skills, accent }) {
  return (
    <CompactChipList
      items={skills}
      accent={accent}
      color="#2d2b3f"
      background={withAlpha(accent, "0f")}
    />
  );
}

function BlueSidebarSkillGroups({ skills, accent }) {
  if (!skills.length) return null;
  return <CompactChipList items={skills} accent={accent} color="#374151" background="#ffffff" />;
}

function CyanSidebarBars({ items, accent = "#ffffff", limit = Number.MAX_SAFE_INTEGER, dataField = "skill" }) {
  return <CompactChipList items={items} accent={accent} limit={limit} dataField={dataField} color="#ffffff" background="rgba(255,255,255,0.12)" borderColor="rgba(255,255,255,0.5)" />;
}

function MintMatrixSkills({ skills, accent, limit = Number.MAX_SAFE_INTEGER, dataField = "skill" }) {
  const visibleSkills = normalizeRenderItems(skills).slice(0, limit);
  if (!visibleSkills.length) return null;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
        gap: "6px 12px",
        alignItems: "start",
      }}
    >
      {visibleSkills.map((skill, index) => (
        <div
          key={cleanDisplayValue(skill) || index}
          data-section={`${dataField}Item`}
          data-field={dataField}
          style={{
            minWidth: 0,
            breakInside: "avoid",
            pageBreakInside: "avoid",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "8px minmax(0, 1fr)",
              gap: 5,
              alignItems: "center",
              color: "#263238",
              fontSize: 11.4,
              fontWeight: 700,
              lineHeight: 1.18,
            }}
          >
            <span style={{ color: accent, fontSize: 9, lineHeight: 1 }}>▶</span>
            <span style={{ minWidth: 0, overflowWrap: "break-word" }}>{skill}</span>
          </div>
          <div style={{ height: 4, background: "rgba(255,255,255,0.7)", marginTop: 3 }}>
            <div style={{ height: "100%", width: `${78 - (index % 4) * 8}%`, background: accent }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function CoralSkillGrid({ skills, accent }) {
  return <CompactChipList items={skills} accent={accent} color="#333" background="#ffffff" />;
}

function NeonChipList({ items, accent, limit = Number.MAX_SAFE_INTEGER, dataField = "skill" }) {
  const visibleItems = normalizeRenderItems(items).slice(0, limit);
  if (!visibleItems.length) return null;
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px 7px",
        alignItems: "center",
      }}
    >
      {visibleItems.map((item, index) => (
        <span
          key={cleanDisplayValue(item) || index}
          data-section={`${dataField}Item`}
          data-field={dataField}
          style={{
            display: "inline-flex",
            maxWidth: "100%",
            border: `1px solid ${withAlpha(accent, "aa")}`,
            borderRadius: 30,
            padding: "3px 9px",
            background: withAlpha(accent, "12"),
            boxShadow: `0 0 10px ${withAlpha(accent, "22")}`,
            color: "#e5fffb",
            fontSize: 11.4,
            fontWeight: 700,
            lineHeight: 1.25,
            letterSpacing: 0,
            overflowWrap: "anywhere",
            breakInside: "avoid",
            pageBreakInside: "avoid",
          }}
        >
          {item}
        </span>
      ))}
    </div>
  );
}

function PurpleExperienceList({ experiences, accent }) {
  if (!experiences.length) return null;
  return (
    <div style={{ display: "grid", gap: 11 }}>
      {experiences.map((experience, index) => {
        const date = formatDateRange(experience.startDate, experience.endDate, experience.years);
        const highlights = getExperienceHighlights(experience, 5);
        return (
          <article key={`${experience.company}-${index}`} className="cv-group" data-section="experienceItem" style={{ breakInside: "auto", pageBreakInside: "auto" }}>
            <div data-field="jobRole" style={{ color: "#222", fontSize: 14, fontWeight: 800 }}>
              {experience.role || "Job Position"}{date ? ` - ${date}` : ""}
            </div>
            <div data-field="company" style={{ color: "#4b5563", fontSize: 12.5, marginTop: 2 }}>{experience.company}</div>
            {highlights.length > 0 && (
              <ul style={{ margin: "6px 0 0", paddingLeft: 16, color: "#555", fontSize: 12.5, lineHeight: 1.38 }}>
                {highlights.map((highlight, highlightIndex) => (
                  <li key={highlightIndex} style={{ marginBottom: 2 }}>{highlight}</li>
                ))}
              </ul>
            )}
          </article>
        );
      })}
    </div>
  );
}

function BlueExperienceList({ experiences, accent }) {
  if (!experiences.length) return null;
  return (
    <div style={{ display: "grid", gap: 11 }}>
      {experiences.map((experience, index) => {
        const highlights = getExperienceHighlights(experience, 4);
        const date = formatDateRange(experience.startDate, experience.endDate, experience.years);
        return (
          <article key={`${experience.company}-${index}`} className="cv-group" data-section="experienceItem" style={{ breakInside: "auto" }}>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 10, alignItems: "baseline" }}>
              <div data-field="jobRole" style={{ color: "#666", fontSize: 15, fontWeight: 800 }}>{experience.role || "Role"}</div>
              {date && <div data-field="years" style={{ color: "#111", fontSize: 12.5, whiteSpace: "nowrap" }}>{date}</div>}
            </div>
            <div data-field="company" style={{ color: "#888", fontSize: 13 }}>{experience.company}</div>
            {highlights.length > 0 && (
              <ul style={{ margin: "6px 0 0", paddingLeft: 22, color: "#818181", fontSize: 12.5, lineHeight: 1.36 }}>
                {highlights.map((highlight, highlightIndex) => (
                  <li key={highlightIndex} style={{ marginBottom: 3, paddingLeft: 5, color: "#818181" }}>
                    <span style={{ color: "#818181" }}>{highlight}</span>
                  </li>
                ))}
              </ul>
            )}
          </article>
        );
      })}
    </div>
  );
}

function BlushExperienceNarrative({ experiences, accent }) {
  if (!experiences.length) return null;
  return (
    <div style={{ display: "grid", gap: 13 }}>
      {experiences.map((experience, index) => {
        const date = formatDateRange(experience.startDate, experience.endDate, experience.years);
        const highlights = getExperienceHighlights(experience, 4);
        return (
          <article key={`${experience.company}-${index}`} className="cv-group" data-section="experienceItem" style={{ breakInside: "auto" }}>
            <div data-field="jobRole" style={{ color: "#2d2b3f", fontSize: 15, fontWeight: 800 }}>
              {experience.role || "Job Position"}{date ? ` - ${date}` : ""}
            </div>
            <div data-field="company" style={{ color: "#6b6470", fontSize: 13, marginTop: 3 }}>
              {experience.company}
            </div>
            {highlights.length > 0 && (
              <ul style={{ margin: "6px 0 0", paddingLeft: 18, color: "#5b6070", fontSize: 13, lineHeight: 1.42 }}>
                {highlights.map((highlight, highlightIndex) => (
                  <li key={highlightIndex} style={{ marginBottom: 3 }}>{highlight}</li>
                ))}
              </ul>
            )}
          </article>
        );
      })}
    </div>
  );
}

function CompactTimelineExperience({ experiences, accent, dark = false, limit = Number.MAX_SAFE_INTEGER }) {
  if (!experiences.length) return null;
  return (
    <div style={{ display: "grid", gap: dark ? 6 : 5 }}>
      {experiences.slice(0, limit).map((experience, index) => {
        const date = formatDateRange(experience.startDate, experience.endDate, experience.years);
        const highlights = getExperienceHighlights(experience, dark ? 2 : 3);
        return (
          <article
            key={`${experience.company}-${index}`}
            className="cv-group"
            data-section="experienceItem"
            style={{
              display: "grid",
              gridTemplateColumns: dark ? "16px minmax(0, 1fr)" : "18px minmax(0, 1fr)",
              gap: 6,
              breakInside: "auto",
            }}
          >
            <div aria-hidden="true" style={{ color: accent, fontSize: dark ? 15 : 10, lineHeight: 1.6 }}>{dark ? "▶" : "■"}</div>
            <div>
              <div data-field="jobRole" style={{ color: dark ? "#ffffff" : "#111", fontSize: dark ? 13 : 12.5, fontWeight: 800, letterSpacing: dark ? 0 : "0.03em" }}>
                {experience.role || "Role"}
              </div>
              <div style={{ color: dark ? "#b8b8b8" : "#404040", fontSize: dark ? 11.5 : 11.5, marginTop: 2 }}>
                <span data-field="company">{experience.company}</span>{date ? <span data-field="years"> | {date}</span> : null}
              </div>
              {highlights.length > 0 && (
                <div style={{ color: dark ? "#d1d5db" : "#6f6f6f", fontSize: dark ? 11.5 : 11.5, lineHeight: 1.38, marginTop: 2 }}>
                  {highlights.map((highlight, highlightIndex) => (
                    <div key={highlightIndex}>{highlight}</div>
                  ))}
                </div>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}

function splitExperienceFlow(experiences, leadingCount = 2) {
  return {
    leadingExperiences: experiences.slice(0, leadingCount),
    remainingExperiences: experiences.slice(leadingCount),
  };
}

export function PurpleFrameTemplate() {
  const { token } = useToken();
  const model = useTemplateModel();
  const accent = getTemplateAccent(token, "#43276F");
  const nameParts = model.name.split(/\s+/);
  const surname = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";
  const firstName = nameParts[0] || model.name;
  const { leadingExperiences, remainingExperiences } = splitExperienceFlow(model.experiences);

  return (
    <Shell padding={36}>
      <header className="cv-section" data-section="header" style={{ marginBottom: 16, position: "relative" }}>
        <h1 data-field="name" style={{ margin: 0, color: accent, fontSize: 34, letterSpacing: "0.04em", fontWeight: 800 }}>
          {firstName} <span style={{ fontWeight: 300 }}>{surname}</span>
        </h1>
        <div data-field="roleTitle" style={{ color: "#4b5563", fontSize: 14, letterSpacing: "0.04em", marginTop: 5, marginBottom: 14 }}>
          {model.role}
        </div>
        <LinkIconStrip model={model} accent={accent} color={accent} background="#ffffff" />
        <div
          style={{
            position: "relative",
            background: accent,
            color: "#ffffff",
            borderRadius: "20px 0 20px 20px",
            padding: "10px 22px 12px",
            width: "76%",
            minHeight: 88,
          }}
        >
          <ProfileTextBlocks
            model={model}
            labelStyle={{ color: "#ffffff", borderBottom: "1px solid rgba(255,255,255,0.72)", display: "inline-block" }}
            textStyle={{ color: "#ede9fe", fontSize: 12, lineHeight: 1.34, maxWidth: 430 }}
          />
          <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, maxWidth: 520 }}>
            <ContactRows items={model.primaryContacts} color="#ffffff" iconColor="#d8b4fe" compact />
          </div>
          <div
            style={{
              position: "absolute",
              right: -148,
              top: -78,
              width: 140,
              height: 170,
              border: `8px solid ${accent}`,
              borderRadius: 18,
              background: "#ffffff",
              display: "grid",
              placeItems: "center",
            }}
          >
            <PhotoFrame data={model.data} initials={model.initials} size={118} square style={{ borderRadius: 10, background: "#ffffff" }} />
          </div>
        </div>
      </header>
      <div style={{ display: "grid", gridTemplateColumns: "210px minmax(0, 1fr)", gap: 22, alignItems: "start" }}>
        <aside style={{ display: "grid", gap: 12 }}>
          {model.education.length > 0 && <section className="cv-section" data-section="education"><SectionTitle accent={accent}>Education</SectionTitle><EducationList education={model.education} accent={accent} compact /></section>}
          {model.skills.length > 0 && <section className="cv-section" data-section="skills"><SectionTitle accent={accent}>Expertise</SectionTitle><PurpleExpertiseList skills={model.skills} accent={accent} /></section>}
          {model.languages.length > 0 && <section className="cv-section" data-section="languages"><SectionTitle accent={accent}>Languages</SectionTitle><PlainBulletList items={model.languages} dataField="language" color="#3f3f46" /></section>}
          {model.achievements.length > 0 && <section className="cv-section" data-section="achievements"><SectionTitle accent={accent}>Achievements</SectionTitle><AchievementList achievements={model.achievements} color="#3f3f46" /></section>}
          {model.certifications.length > 0 && <section className="cv-section" data-section="certifications"><SectionTitle accent={accent}>Certifications</SectionTitle><LinkedList items={model.certifications} dataField="certification" color="#3f3f46" accent={accent} /></section>}
          {model.hobbies.length > 0 && <section className="cv-section" data-section="hobbies"><SectionTitle accent={accent}>Hobbies</SectionTitle><PlainBulletList items={model.hobbies} dataField="hobby" color="#3f3f46" /></section>}
          {getReferenceItems(model).length > 0 && <section className="cv-section" data-section="references"><LinkedList items={getReferenceItems(model)} color="#3f3f46" accent={accent} /></section>}
        </aside>
        <main>
          <section className="cv-section" data-section="experience">
            <SectionTitle accent={accent}>Work Experience</SectionTitle>
            <PurpleExperienceList experiences={leadingExperiences} accent={accent} />
          </section>
        </main>
      </div>
      {remainingExperiences.length > 0 && (
        <section className="cv-section" data-section="experience" style={{ marginTop: 12 }}>
          <SectionTitle accent={accent}>Work Experience Continued</SectionTitle>
          <PurpleExperienceList experiences={remainingExperiences} accent={accent} />
        </section>
      )}
    </Shell>
  );
}

export function BlushCloudTemplate() {
  const { token } = useToken();
  const model = useTemplateModel();
  const accent = getTemplateAccent(token, "#7B5A4C");
  const blushSurface = themedTint(token, "#F2D7CF", "1f");
  const blushHalo = themedTint(token, "#FFF7F2", "12");

  return (
    <Shell padding={0} background="#ffffff">
      <header data-section="header" className="cv-section" style={{ background: blushSurface, minHeight: 112, position: "relative", padding: "18px 36px 12px 184px" }}>
        <div style={{ position: "absolute", left: 22, top: -24, width: 176, height: 176, borderRadius: "50%", background: blushHalo }} />
        <PhotoFrame data={model.data} initials={model.initials} size={82} style={{ position: "absolute", left: 70, top: 34, border: `4px solid ${accent}` }} />
        <h1 data-field="name" style={{ margin: 0, color: accent, fontSize: 26, fontWeight: 800 }}>{model.name}</h1>
        <div data-field="roleTitle" style={{ marginTop: 7, display: "inline-block", border: `1px solid ${withAlpha(accent, "66")}`, padding: "3px 22px", fontSize: 12.5, fontStyle: "italic" }}>{model.role}</div>
        <LinkIconStrip model={model} accent={accent} color={accent} background="rgba(255,255,255,0.65)" />
        <div style={{ marginTop: 9, background: accent, color: "#ffffff", marginLeft: -36, marginRight: -36, padding: "6px 36px" }}>
          <ContactRows items={model.primaryContacts.slice(1, 3)} color="#ffffff" iconColor="#ffffff" compact />
        </div>
      </header>
      <main style={{ padding: "28px 56px 32px" }}>
        <h2 data-field="roleTitle" style={{ textAlign: "center", color: "#27263A", fontSize: 20, margin: "0 0 12px" }}>
          {model.role} Resume
        </h2>
        <section className="cv-section" data-section="contact" style={{ borderTop: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb", padding: "10px 0", marginBottom: 14 }}>
          <ContactRows items={model.contacts} color="#4b5563" iconColor={accent} compact />
        </section>
        {hasProfileText(model) && <section className="cv-section" data-section="summary" style={{ marginBottom: 14 }}><h3 style={{ color: "#27263A", fontSize: 16, margin: "0 0 6px" }}>I. Profile Details</h3><ProfileTextBlocks model={model} textStyle={{ color: "#5b6070", fontSize: 12.4, lineHeight: 1.34 }} labelStyle={{ color: "#27263A" }} /></section>}
        {model.skills.length > 0 && <section className="cv-section" data-section="skills" style={{ marginBottom: 14, borderBottom: "1px solid #e5e7eb", paddingBottom: 10 }}><h3 style={{ color: "#27263A", fontSize: 16, margin: "0 0 6px" }}>II. Skills</h3><BlushSkillNarrative skills={model.skills} accent={accent} /></section>}
        {model.education.length > 0 && <section className="cv-section" data-section="education" style={{ marginBottom: 14, borderBottom: "1px solid #e5e7eb", paddingBottom: 10 }}><h3 style={{ color: "#27263A", fontSize: 16, margin: "0 0 6px" }}>III. Education</h3><EducationList education={model.education} accent={accent} /></section>}
        {model.experiences.length > 0 && <section className="cv-section" data-section="experience"><h3 style={{ color: "#27263A", fontSize: 18, margin: "0 0 8px" }}>IV. Experience</h3><BlushExperienceNarrative experiences={model.experiences} accent={accent} /></section>}
        <SupplementalSections model={model} accent={accent} color="#4b5563" titleColor="#27263A" panelStyle={{ borderTop: "1px solid #e5e7eb", paddingTop: 10 }} />
      </main>
    </Shell>
  );
}

export function BlueSidebarTemplate() {
  const { token } = useToken();
  const model = useTemplateModel();
  const accent = getTemplateAccent(token, "#3E66B1");
  const { leadingExperiences, remainingExperiences } = splitExperienceFlow(model.experiences);

  return (
    <Shell padding={28}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 218px", gap: 22 }}>
        <main>
          <header data-section="header" className="cv-section" style={{ marginBottom: 16 }}>
            <h1 data-field="name" style={{ margin: 0, color: accent, fontSize: 30, fontWeight: 800 }}>{model.name}</h1>
            <div data-field="roleTitle" style={{ fontSize: 16, color: "#6b7280", marginTop: 4 }}>{model.role}</div>
            <LinkIconStrip model={model} accent={accent} color={accent} />
            <ProfileTextBlocks model={model} style={{ marginTop: 10 }} textStyle={{ color: "#777", lineHeight: 1.38 }} labelStyle={{ color: accent }} />
          </header>
          <section className="cv-section" data-section="experience">
            <SectionTitle accent={accent} color={accent}>Work Experience</SectionTitle>
            <BlueExperienceList experiences={leadingExperiences} accent={accent} />
          </section>
        </main>
        <aside style={{ background: "#F1F3F7", padding: "16px 14px", alignSelf: "stretch" }}>
          <section className="cv-section" data-section="contact" style={{ marginBottom: 16 }}><SectionTitle accent="#d1d5db" color={accent}>Contact</SectionTitle><ContactRows items={model.contacts} color="#777" iconColor={accent} compact /></section>
          <section className="cv-section" data-section="skills" style={{ marginBottom: 16 }}><SectionTitle accent="#d1d5db" color={accent}>Skills</SectionTitle><BlueSidebarSkillGroups skills={model.skills} accent={accent} /></section>
          {model.languages.length > 0 && <section className="cv-section" data-section="languages" style={{ marginBottom: 16 }}><SectionTitle accent="#d1d5db" color={accent}>Languages</SectionTitle><PlainBulletList items={model.languages} dataField="language" color="#777" marker="•" /></section>}
          {model.education.length > 0 && <section className="cv-section" data-section="education" style={{ marginBottom: 16 }}><SectionTitle accent="#d1d5db" color={accent}>Education</SectionTitle><EducationList education={model.education} accent={accent} compact /></section>}
          {model.achievements.length > 0 && <section className="cv-section" data-section="achievements"><SectionTitle accent="#d1d5db" color={accent}>Achievements</SectionTitle><AchievementList achievements={model.achievements} color="#777" /></section>}
          <SupplementalSections model={model} accent={accent} color="#777" titleColor={accent} skip={{ achievements: true, languages: true }} />
        </aside>
      </div>
      {remainingExperiences.length > 0 && (
        <section className="cv-section" data-section="experience" style={{ marginTop: 14 }}>
          <SectionTitle accent={accent} color={accent}>Work Experience Continued</SectionTitle>
          <BlueExperienceList experiences={remainingExperiences} accent={accent} />
        </section>
      )}
    </Shell>
  );
}

export function CyanProfileTemplate() {
  const { token } = useToken();
  const model = useTemplateModel();
  const accent = getTemplateAccent(token, "#0D97C9");
  const dividerColor = hasThemeAccent(token) ? withAlpha(accent, "88") : "#9ca3af";
  const railNameTypography = getRailNameTypography(model.name);
  const { leadingExperiences, remainingExperiences } = splitExperienceFlow(model.experiences);

  return (
    <Shell padding={0}>
      <div data-cyan-profile-rail style={{ display: "grid", gridTemplateColumns: "235px minmax(0, 1fr)" }}>
        <aside style={{ background: accent, color: "#ffffff", padding: "34px 28px" }}>
          <PhotoFrame data={model.data} initials={model.initials} size={92} style={{ margin: "0 auto 18px", border: "5px solid rgba(255,255,255,0.8)" }} />
          <h1
            data-field="name"
            style={{
              textAlign: "center",
              fontSize: `clamp(17px, 2.35vw, ${railNameTypography.fontSize}px)`,
              letterSpacing: railNameTypography.letterSpacing,
              lineHeight: railNameTypography.lineHeight,
              fontWeight: 600,
              margin: "0 auto 12px",
              maxWidth: "100%",
              overflowWrap: "break-word",
              wordBreak: "normal",
              hyphens: "none",
              textWrap: "balance",
            }}
          >
            {model.name}
          </h1>
          <LinkIconStrip model={model} accent="#ffffff" color="#ffffff" background="rgba(255,255,255,0.14)" align="center" />
          <ProfileTextBlocks
            model={model}
            style={{ margin: "0 0 22px" }}
            labelStyle={{ color: "#ffffff" }}
            textStyle={{
              color: "#eef9ff",
              textAlign: "left",
              fontSize: 11.6,
              lineHeight: 1.38,
              maxWidth: "100%",
            }}
          />
          <section className="cv-section" data-section="contact" style={{ marginBottom: 24 }}><SectionTitle color="#ffffff" accent="#ffffff">Contact</SectionTitle><ContactRows items={model.contacts} color="#ffffff" iconColor="#ffffff" compact /></section>
          {model.education.length > 0 && <section className="cv-section" data-section="education" style={{ marginBottom: 20 }}><SectionTitle color="#ffffff" accent="#ffffff">Education</SectionTitle><EducationList education={model.education} accent="#ffffff" compact titleColor="#ffffff" mutedColor="#e8f9ff" /></section>}
          <section className="cv-section" data-section="skills" style={{ marginBottom: 20 }}><SectionTitle color="#ffffff" accent="#ffffff">Skills</SectionTitle><CyanSidebarBars items={model.skills} accent="#ffffff" /></section>
          <section className="cv-section" data-section="languages" style={{ marginBottom: 20 }}><SectionTitle color="#ffffff" accent="#ffffff">Languages</SectionTitle><CyanSidebarBars items={model.languages.length ? model.languages : ["English"]} accent="#ffffff" dataField="language" /></section>
          {model.hobbies.length > 0 && <section className="cv-section" data-section="hobbies"><SectionTitle color="#ffffff" accent="#ffffff">Hobbies</SectionTitle><PlainBulletList items={model.hobbies} dataField="hobby" color="#ffffff" marker="·" /></section>}
        </aside>
        <main style={{ padding: "46px 42px" }}>
          <section className="cv-section" data-section="summary" style={{ marginBottom: 22, maxWidth: 520 }}><SectionTitle line accent={dividerColor} color="#8a8a8a">Personal Profile</SectionTitle><ProfileTextBlocks model={model} textStyle={{ color: "#6f7683", textAlign: "left", fontSize: 12.4, lineHeight: 1.4 }} labelStyle={{ color: "#8a8a8a" }} /></section>
          {model.achievements.length > 0 && <section className="cv-section" data-section="achievements" style={{ marginBottom: 22 }}><SectionTitle line accent={dividerColor} color="#8a8a8a">Achievements</SectionTitle><AchievementList achievements={model.achievements} color="#8a8a8a" /></section>}
          <section className="cv-section" data-section="experience"><SectionTitle line accent={dividerColor} color="#8a8a8a">Work Experience</SectionTitle><CompactTimelineExperience experiences={leadingExperiences} accent={accent} /></section>
          <SupplementalSections model={model} accent={accent} color="#6f7683" titleColor="#8a8a8a" skip={{ achievements: true, hobbies: true, languages: true }} />
        </main>
      </div>
      {remainingExperiences.length > 0 && (
        <section className="cv-section" data-section="experience" style={{ padding: "0 42px 34px 42px" }}>
          <SectionTitle line accent={dividerColor} color="#8a8a8a">Work Experience Continued</SectionTitle>
          <CompactTimelineExperience experiences={remainingExperiences} accent={accent} />
        </section>
      )}
    </Shell>
  );
}

export function MintCompactTemplate() {
  const { token } = useToken();
  const model = useTemplateModel();
  const accent = getTemplateAccent(token, "#79AEB7");
  const pageTint = themedTint(token, "#DCEFF1", "18");
  const panelTint = themedTint(token, "rgba(255,255,255,0.45)", "10");

  return (
    <Shell background={pageTint} padding={20}>
      <header data-section="header" className="cv-section" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 78px", gap: 12, alignItems: "center", marginBottom: 8 }}>
        <div>
          <h1 data-field="name" style={{ margin: 0, color: "#334155", fontSize: 24, fontWeight: 500, letterSpacing: "0.08em" }}>{model.name}</h1>
          <div data-field="roleTitle" style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.12em" }}>{model.role}</div>
          <LinkIconStrip model={model} accent={accent} color="#475569" background="rgba(255,255,255,0.45)" />
          <div style={{ marginTop: 6, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 5 }}><ContactRows items={model.primaryContacts} compact color="#475569" iconColor={accent} /></div>
        </div>
        <PhotoFrame data={model.data} initials={model.initials} size={74} style={{ background: "#ffffff", color: accent, fontSize: 24, border: `7px solid ${withAlpha(accent, "24")}`, justifySelf: "end" }} />
      </header>
      <div style={{ background: panelTint, padding: "7px 10px", marginBottom: 6 }}>
        <section className="cv-section" data-section="skills"><SectionTitle accent={accent} color="#64748b">Skills</SectionTitle><MintMatrixSkills skills={model.skills} accent={accent} /></section>
      </div>
      <section className="cv-section" data-section="experience" style={{ marginBottom: 6 }}><SectionTitle accent={accent} color="#64748b">Work Experience</SectionTitle><CompactTimelineExperience experiences={model.experiences} accent={accent} /></section>
      {model.education.length > 0 && <section className="cv-section" data-section="education" style={{ background: panelTint, padding: 8, marginBottom: 6 }}><SectionTitle accent={accent} color="#64748b">Education</SectionTitle><EducationList education={model.education} accent={accent} compact /></section>}
      {hasProfileText(model) && <section className="cv-section" data-section="summary" style={{ background: panelTint, padding: "7px 10px", marginBottom: 6 }}><SectionTitle accent={accent} color="#64748b">Profile</SectionTitle><ProfileTextBlocks model={model} textStyle={{ color: "#475569", fontSize: 12.2, lineHeight: 1.34 }} labelStyle={{ color: "#64748b" }} /></section>}
      <section className="cv-section" data-section="languages" style={{ background: panelTint, padding: "7px 10px", marginBottom: 6 }}><SectionTitle accent={accent} color="#64748b">Languages</SectionTitle><MintMatrixSkills skills={model.languages.length ? model.languages : ["English"]} accent={accent} dataField="language" /></section>
      {model.achievements.length > 0 && <section className="cv-section" data-section="achievements" style={{ background: panelTint, padding: 8 }}><SectionTitle accent={accent} color="#64748b">Interests & Highlights</SectionTitle><AchievementList achievements={model.achievements} color="#475569" /></section>}
      <SupplementalSections model={model} accent={accent} color="#475569" titleColor="#64748b" panelStyle={{ background: panelTint, padding: 8 }} skip={{ achievements: true, languages: true }} />
    </Shell>
  );
}

export function CoralSidebarTemplate() {
  const { token } = useToken();
  const model = useTemplateModel();
  const accent = getTemplateAccent(token, "#FF9464");
  const { leadingExperiences, remainingExperiences } = splitExperienceFlow(model.experiences);

  return (
    <Shell padding={0}>
      <div style={{ display: "grid", gridTemplateColumns: "180px minmax(0, 1fr)" }}>
        <aside style={{ background: accent, padding: "28px 20px", color: "#513224" }}>
          <div style={{ width: 74, height: 74, border: "2px solid rgba(255,255,255,0.8)", display: "grid", placeItems: "center", fontSize: 24, marginBottom: 18 }}>CV</div>
          <h1 data-field="name" style={{ margin: "0 0 16px", fontSize: 17, letterSpacing: "0.14em", lineHeight: 1.35, fontWeight: 500 }}>{model.name}</h1>
          <LinkIconStrip model={model} accent="#513224" color="#513224" background="rgba(255,255,255,0.24)" />
          <section className="cv-section" data-section="summary" style={{ marginBottom: 20 }}><SectionTitle accent="#8A563E" color="#513224">Profile</SectionTitle><ProfileTextBlocks model={model} textStyle={{ fontSize: 11.6, color: "#5f3b2c" }} labelStyle={{ color: "#513224" }} /></section>
          <section className="cv-section" data-section="contact" style={{ marginBottom: 22 }}><SectionTitle accent="#8A563E" color="#513224">Contact</SectionTitle><ContactRows items={model.contacts} color="#5f3b2c" iconColor="#5f3b2c" compact /></section>
          {model.languages.length > 0 && <section className="cv-section" data-section="languages" style={{ marginBottom: 20 }}><SectionTitle accent="#8A563E" color="#513224">Languages</SectionTitle><PlainBulletList items={model.languages} dataField="language" color="#5f3b2c" /></section>}
          {model.achievements.length > 0 && <section className="cv-section" data-section="achievements"><SectionTitle accent="#8A563E" color="#513224">Achievements</SectionTitle><AchievementList achievements={model.achievements.slice(0, 2)} color="#5f3b2c" /></section>}
          <SupplementalSections model={model} accent="#8A563E" color="#5f3b2c" titleColor="#513224" skip={{ achievements: true, languages: true }} />
        </aside>
        <main style={{ padding: "26px 32px" }}>
          <section className="cv-section" data-section="experience" style={{ marginBottom: 20 }}><SectionTitle accent={accent} color="#7c7c7c">Work Experience</SectionTitle><CompactTimelineExperience experiences={leadingExperiences} accent={accent} /></section>
          {model.education.length > 0 && <section className="cv-section" data-section="education" style={{ marginBottom: 20 }}><SectionTitle accent={accent} color="#7c7c7c">Education</SectionTitle><EducationList education={model.education} accent={accent} /></section>}
          <section className="cv-section" data-section="skills"><SectionTitle accent={accent} color="#7c7c7c">Skills</SectionTitle><CoralSkillGrid skills={model.skills} accent={accent} /></section>
        </main>
      </div>
      {remainingExperiences.length > 0 && (
        <section className="cv-section" data-section="experience" style={{ padding: "0 32px 28px 32px" }}>
          <SectionTitle accent={accent} color="#7c7c7c">Work Experience Continued</SectionTitle>
          <CompactTimelineExperience experiences={remainingExperiences} accent={accent} />
        </section>
      )}
    </Shell>
  );
}

export function NeonDarkTemplate() {
  const { token } = useToken();
  const model = useTemplateModel();
  const accent = getTemplateAccent(token, "#00B8A9");
  const { leadingExperiences, remainingExperiences } = splitExperienceFlow(model.experiences);

  return (
    <Shell background="#050505" color="#E5E7EB" padding={24}>
      <header data-section="header" className="cv-section" style={{ display: "grid", gridTemplateColumns: "152px minmax(0, 1fr)", gap: 22, alignItems: "center", marginBottom: 20 }}>
        <PhotoFrame data={model.data} initials={model.initials} size={118} style={{ border: `1px solid ${accent}`, background: "#202020" }} />
        <div>
          <h1 data-field="name" style={{ margin: 0, color: "#ffffff", fontSize: 34, fontWeight: 800 }}>{model.name}</h1>
          <div data-field="roleTitle" style={{ color: accent, fontSize: 14, letterSpacing: "0.08em", marginTop: 5 }}>{model.role}</div>
          <div style={{ border: `1px solid ${accent}`, marginTop: 12, padding: "6px 10px" }}>
            <ContactRows items={model.primaryContacts} color="#e5e7eb" iconColor={accent} compact />
            <LinkIconStrip model={model} accent={accent} color="#e5e7eb" background="rgba(0,184,169,0.12)" />
          </div>
        </div>
      </header>
      <div style={{ display: "grid", gridTemplateColumns: "220px minmax(0, 1fr)", gap: 22 }}>
        <aside style={{ display: "grid", gap: 16 }}>
          <section className="cv-section" data-section="skills"><SectionTitle color={accent} accent={accent}>Expertise</SectionTitle><NeonChipList items={model.skills} accent={accent} /></section>
          <section className="cv-section" data-section="languages"><SectionTitle color={accent} accent={accent}>Language</SectionTitle><NeonChipList items={model.languages.length ? model.languages : ["English"]} accent={accent} dataField="language" /></section>
          {model.achievements.length > 0 && <section className="cv-section" data-section="achievements"><SectionTitle color={accent} accent={accent}>Highlights</SectionTitle><AchievementList achievements={model.achievements} color="#d1d5db" /></section>}
          <SupplementalSections model={model} accent={accent} color="#d1d5db" titleColor={accent} skip={{ achievements: true, languages: true }} />
        </aside>
        <main>
          {hasProfileText(model) && <section className="cv-section" data-section="summary" style={{ marginBottom: 20 }}><SectionTitle color={accent} accent={accent}>Profile</SectionTitle><ProfileTextBlocks model={model} textStyle={{ color: "#d1d5db" }} labelStyle={{ color: accent }} /></section>}
          <section className="cv-section" data-section="experience" style={{ marginBottom: 20 }}><SectionTitle color={accent} accent={accent}>Work Experience</SectionTitle><CompactTimelineExperience experiences={leadingExperiences} accent={accent} dark /></section>
          {model.education.length > 0 && <section className="cv-section" data-section="education"><SectionTitle color={accent} accent={accent}>Education</SectionTitle><EducationList education={model.education} accent={accent} titleColor="#ffffff" mutedColor="#d1d5db" /></section>}
        </main>
      </div>
      {remainingExperiences.length > 0 && (
        <section className="cv-section" data-section="experience" style={{ marginTop: 16 }}>
          <SectionTitle color={accent} accent={accent}>Work Experience Continued</SectionTitle>
          <CompactTimelineExperience experiences={remainingExperiences} accent={accent} dark />
        </section>
      )}
    </Shell>
  );
}
